import {
    Avatar as AvatarComponent,
    AvatarFallback,
    AvatarImage,
  } from "@/components/ui/avatar"
import { cn } from "@/lib/utils"

type AvatarSize = "xs" | "sm" | "md" | "lg"

const sizeClasses: Record<AvatarSize, string> = {
  xs: "size-5",
  sm: "size-6",
  md: "size-8",
  lg: "size-10",
}

const textSizeClasses: Record<AvatarSize, string> = {
  xs: "text-[9px] font-semibold",
  sm: "text-[10px] font-semibold",
  md: "text-xs font-medium",
  lg: "text-sm font-medium",
}

export function Avatar({ 
  src, 
  alt, 
  fallback, 
  className,
  size = "md"
}: { 
  src?: string
  alt: string
  fallback?: string | React.ReactNode
  className?: string
  size?: AvatarSize
}) {
    const getColors = (name: string | undefined): { bg: string, text: string } => {
      if (!name) {
        return { bg: `#743E8E`, text: `#FFFFFF` } // Default purple with white text
      }
      const initial = name.charAt(0).toUpperCase()
      
      if (initial >= 'A' && initial <= 'D') {
        return { bg: `#743E8E`, text: `#FFFFFF` } // Purple with white text
      } else if (initial >= 'E' && initial <= 'H') {
        return { bg: `#E84068`, text: `#FFFFFF` } // Pink/Red with white text
      } else if (initial >= 'I' && initial <= 'M') {
        return { bg: `#EF8D37`, text: `#FFFFFF` } // Orange with white text
      } else if (initial >= 'N' && initial <= 'R') {
        return { bg: `#EDCA1C`, text: `#1F1F1F` } // Yellow with dark text
      } else if (initial >= 'S' && initial <= 'V') {
        return { bg: `#71AD68`, text: `#FFFFFF` } // Light Green with white text
      } else if (initial >= 'W' && initial <= 'Z') {
        return { bg: `#3E8F55`, text: `#FFFFFF` } // Dark Green with white text
      }
      
      // Fallback to purple with white text for non-alphabetic characters
      return { bg: `#743E8E`, text: `#FFFFFF` }
    };

  let fallbackContent = fallback
  let colors = { bg: '#F3F4F6', text: '#1F1F1F' } // Default light gray for icons
  
  if (!fallback) {
    fallbackContent = alt.charAt(0).toUpperCase()
    colors = getColors(typeof fallbackContent === 'string' ? fallbackContent : undefined)
  } else if (typeof fallback === 'string') {
    colors = getColors(fallback)
  }

  // For small sizes, use single letter only
  const displayContent = (size === "xs" || size === "sm") && typeof fallbackContent === 'string' 
    ? fallbackContent.charAt(0) 
    : fallbackContent
  
  return (
    <AvatarComponent className={cn("rounded-sm", sizeClasses[size], className)}>
      <AvatarImage src={src} alt={alt} className="rounded-sm" />
      <AvatarFallback 
        className={cn("rounded-sm", textSizeClasses[size])} 
        style={{ backgroundColor: colors.bg, color: colors.text }}
      >
        {displayContent}
      </AvatarFallback>
    </AvatarComponent>
  )
}

export default Avatar;